import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm, useWatch } from 'react-hook-form';
import { TreeViewNode } from '../../../../../../@shared/Fields/models';
import useRequest from '../../../../../../@shared/Requests/useRequest';
import yup from '../../../../../../config/yup/customYup';
import categoryEndpoints from '../../../../../category/category.endpoints';
import { Category } from '../../../../../category/features/CategoryForm/types';
import classificationEndpoints from '../../../../classification.endpoint';
import { BoxClassificationAddForm } from '../boxClassificationAddFormSteps.types';

interface UseBoxClassificationAddFormStep3Props {
  defaultValue?: BoxClassificationAddForm | BoxClassificationAddForm.Step3;
  onSubmit?: (e: BoxClassificationAddForm.Step3) => void;
}

const useBoxClassificationAddFormStep3 = (props?: UseBoxClassificationAddFormStep3Props) => {
  const r = useRequest();
  const { control, formState, handleSubmit } = useForm<BoxClassificationAddForm.Step3>({
    defaultValues: (props?.defaultValue as any)?.step3 ?? props?.defaultValue ?? { categories: [] },
    resolver: yupResolver(
      yup.object({
        categories: yup
          .array(
            yup.object({
              name: yup.string().required(),
              isMigration: yup.boolean().required(),
              isDocumentCategory: yup.bool().nullable(),
              parentId: yup.object().nullable(),
            })
          )
          .required()
          .min(1, 'é necessario escolher associar ao menos categoria'),
      })
    ),
  });
  const requestTreeView = useRequest<{ categories: Category[]; parents: TreeViewNode[] }>(
    categoryEndpoints.listCategoriesCreateOrSelectOptions()
  );
  requestTreeView.useFetchBy('init');

  const controller = useController({ name: 'categories', control });
  const categories = useWatch({
    control,
    name: 'categories',
  });

  const onSubmit = (data: BoxClassificationAddForm.Step3) => {
    const a = props?.defaultValue as any;
    r.setRequestConfig(
      classificationEndpoints.add({
        ...(a.step1 ?? {}),
        ...(a.step2 ?? {}),
        categories: data.categories,
      } as any)
    );
  };

  r.useFetchBy(r.requestConfig);

  // Ensure categories is always an array and handle both camelCase and PascalCase
  // The API returns { Categories: CategoryOutputModel[], Parents: CheckTree[] } (PascalCase)
  // or { categories: CategoryOutputModel[], parents: CheckTree[] } (camelCase)
  const successData = requestTreeView.successData as any;
  let categoriesData = successData?.categories ?? successData?.Categories ?? [];

  // If categories array is empty but parents (CheckTree) has data, extract categories from parents
  if (categoriesData.length === 0) {
    const parentsData = successData?.parents ?? successData?.Parents ?? [];
    const extractCategoriesFromTree = (nodes: any[]): any[] => {
      const result: any[] = [];
      nodes.forEach((node: any) => {
        if (node.value && node.label) {
          result.push({
            categoryId: parseInt(node.value, 10),
            name: node.label,
            isMigration: false,
            isDocumentCategory: null,
            parentId: null,
          });
        }
        if (node.children && Array.isArray(node.children) && node.children.length > 0) {
          result.push(...extractCategoriesFromTree(node.children));
        }
      });
      return result;
    };
    categoriesData = extractCategoriesFromTree(parentsData);
  }

  const normalizedCategories = Array.isArray(categoriesData)
    ? categoriesData.map((cat: any) => {
        const normalized = {
          categoryId: cat.categoryId || cat.CategoryId,
          name: cat.name || cat.Name,
          isMigration: cat.isMigration ?? cat.IsMigration ?? false,
          isDocumentCategory: cat.isDocumentCategory ?? cat.IsDocumentCategory ?? null,
          parentId: cat.parentId ?? cat.ParentId ?? null,
          ...cat, // Keep all other properties
        };
        return normalized;
      })
    : [];

  // Filter out categories that are already in the form (already associated)
  const existingCategoryIds = new Set(
    (categories || []).filter((cat: any) => cat?.categoryId).map((cat: any) => cat.categoryId)
  );

  const availableCategories = normalizedCategories.filter(
    (cat: any) => !existingCategoryIds.has(cat.categoryId)
  );

  return {
    control,
    handleSubmit: handleSubmit(onSubmit),
    isEmpty: Array.isArray(formState.errors.categories)
      ? formState.errors.categories.length === 0
      : formState.errors.categories,
    errors: formState.errors,
    success: r.success,
    successData: r.successData,
    parents: successData?.parents ?? successData?.Parents ?? [],
    categories: availableCategories,
    loading: requestTreeView.loading,
    fields: { categories, onCategoriesChange: controller.field.onChange },
  };
};

export default useBoxClassificationAddFormStep3;
